import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import {
  Phone, Mail, Calendar, MessageSquare, FileText, Repeat, Ticket, Target,
  StickyNote, FileSignature, ScrollText, Send, Users, Clock, KeyRound, PhoneMissed, UserX,
  PhoneIncoming, PhoneOutgoing, PhoneOff, Voicemail, PhoneCall,
} from "lucide-react";
import { api } from "../api";

export function eur(v) {
  return new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(v || 0);
}

export function dt(s) {
  if (!s) return "-";
  try {
    return new Date(s).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" });
  } catch (e) {
    return s;
  }
}

export function PageHeader({ overline, title, action, subtitle }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between pb-4 mb-5 border-b border-slate-200/80 gap-3 flex-wrap">
      <div className="min-w-0">
        {overline && (
          <div className="text-blue uppercase text-[10px] font-semibold tracking-[0.22em] mb-1">{overline}</div>
        )}
        <h1 className="text-navy text-xl font-bold tracking-tight leading-tight">{title}</h1>
        {subtitle && <p className="text-[13px] text-slate-500 mt-1 max-w-2xl">{subtitle}</p>}
      </div>
      {action && <div className="flex items-center gap-2">{action}</div>}
    </div>
  );
}

// ClickUp-inspired rainbow pill palette. Cada estado tem tons proprios.
const STATE_STYLE = {
  // sucesso / concluido
  ativa: "bg-green-50 text-green-700 border-green-200",
  ativo: "bg-green-50 text-green-700 border-green-200",
  paga: "bg-green-50 text-green-700 border-green-200",
  pago: "bg-green-50 text-green-700 border-green-200",
  concluido: "bg-emerald-50 text-emerald-700 border-emerald-200",
  saudavel: "bg-emerald-50 text-emerald-700 border-emerald-200",
  aprovado: "bg-emerald-50 text-emerald-700 border-emerald-200",
  validada: "bg-emerald-50 text-emerald-700 border-emerald-200",
  resolvido: "bg-emerald-50 text-emerald-700 border-emerald-200",
  enviado: "bg-emerald-50 text-emerald-700 border-emerald-200",
  verde: "bg-emerald-50 text-emerald-700 border-emerald-200",
  // aberto / em curso
  aberto: "bg-blue-50 text-blue-700 border-blue-200",
  em_curso: "bg-amber-50 text-amber-700 border-amber-200",
  em_provisionamento: "bg-sky-50 text-sky-700 border-sky-200",
  emitida: "bg-sky-50 text-sky-700 border-sky-200",
  criada_online: "bg-sky-50 text-sky-700 border-sky-200",
  online: "bg-sky-50 text-sky-700 border-sky-200",
  aguarda_pagamento: "bg-amber-50 text-amber-700 border-amber-200",
  assistida: "bg-indigo-50 text-indigo-700 border-indigo-200",
  // aguardar / trial
  pendente: "bg-amber-50 text-amber-700 border-amber-200",
  trial: "bg-amber-50 text-amber-700 border-amber-200",
  validacao_tecnica: "bg-amber-50 text-amber-700 border-amber-200",
  a_aguardar_cliente: "bg-teal-50 text-teal-700 border-teal-200",
  em_atraso: "bg-orange-50 text-orange-700 border-orange-200",
  ambar: "bg-amber-50 text-amber-700 border-amber-200",
  // negativos
  vencida: "bg-red-50 text-red-700 border-red-200",
  suspensa: "bg-red-50 text-red-700 border-red-200",
  cancelada: "bg-red-50 text-red-700 border-red-200",
  falhou: "bg-red-50 text-red-700 border-red-200",
  recusada_tecnica: "bg-red-50 text-red-700 border-red-200",
  anulada: "bg-red-50 text-red-700 border-red-200",
  vermelho: "bg-red-50 text-red-700 border-red-200",
  // neutro / desligado
  fechado: "bg-slate-100 text-slate-600 border-slate-200",
  inativa: "bg-slate-100 text-slate-600 border-slate-200",
  nao_configurado: "bg-slate-100 text-slate-600 border-slate-200",
};

export function Badge({ value, className = "" }) {
  const cls = STATE_STYLE[value] || "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[11px] font-semibold uppercase tracking-wide ${cls} ${className}`}>
      {String(value || "-").replace(/_/g, " ")}
    </span>
  );
}

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250];

export function Table({ columns, rows, testid, empty = "Sem registos.", compact = true, rowClass, paginate = true }) {
  const cellCls = compact ? "px-3 py-2 align-middle" : "px-4 py-3 align-middle";
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const prevCount = useRef(rows.length);
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  useEffect(() => {
    if (prevCount.current !== rows.length) { prevCount.current = rows.length; setPage(1); }
    else if (page > totalPages) setPage(1);
  }, [rows.length, totalPages, page]);
  const pageRows = paginate ? rows.slice((page - 1) * pageSize, page * pageSize) : rows;
  const from = rows.length === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, rows.length);
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white shadow-sm" data-testid={testid}>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-slate-50/80 border-b border-slate-200 text-left">
              {columns.map((c) => (
                <th key={c.key} className={`${cellCls} text-[10px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap`}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="px-4 py-10 text-center text-slate-400 text-sm">
                  {empty}
                </td>
              </tr>
            )}
            {pageRows.map((row, i) => (
              <motion.tr
                key={row.id || i}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: Math.min(i, 15) * 0.015 }}
                className={`border-b border-slate-100 last:border-b-0 text-slate-800 transition-colors ${row.onClick ? "cursor-pointer" : ""} ${rowClass ? rowClass(row) : "hover:bg-blue-50/30"}`}
                onClick={row.onClick}
              >
                {columns.map((c) => (
                  <td key={c.key} className={cellCls}>
                    {c.render ? c.render(row) : row[c.key]}
                  </td>
                ))}
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
      {paginate && rows.length > PAGE_SIZE_OPTIONS[0] && (
        <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-t border-slate-200 bg-slate-50/60 text-xs text-slate-500" data-testid={`${testid || "table"}-pagination`}>
          <span>A mostrar <b className="text-slate-700">{from}-{to}</b> de <b className="text-slate-700">{rows.length}</b> registos</span>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1">
              Por pagina:
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                data-testid={`${testid || "table"}-page-size`}
                className="px-2 py-1 border border-slate-200 rounded bg-white text-slate-700 font-semibold"
              >
                {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              data-testid={`${testid || "table"}-prev-page`}
              className="px-2 py-1 border border-slate-200 rounded bg-white text-slate-600 font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-100"
            >Anterior</button>
            <span className="text-slate-600 font-semibold">Pagina {page} / {totalPages}</span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              data-testid={`${testid || "table"}-next-page`}
              className="px-2 py-1 border border-slate-200 rounded bg-white text-slate-600 font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-100"
            >Seguinte</button>
          </div>
        </div>
      )}
    </div>
  );
}

export function Btn({ children, onClick, variant = "primary", type = "button", testid, disabled, size = "md", className = "" }) {
  const styles = {
    primary: `bg-blue text-white hover:bg-[var(--blue-hover)] shadow-sm`,
    ghost: `bg-white text-slate-700 border border-slate-300 hover:bg-slate-50`,
    subtle: `text-slate-600 hover:text-slate-900 hover:bg-slate-100`,
    danger: `bg-red-600 text-white hover:bg-red-700`,
  };
  const sizes = {
    sm: "px-2.5 py-1 text-[12px]",
    md: "px-3 py-1.5 text-[13px]",
    lg: "px-4 py-2 text-[14px]",
  };
  const base = "inline-flex items-center gap-1.5 rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
  return (
    <button type={type} onClick={onClick} disabled={disabled} data-testid={testid} className={`${base} ${sizes[size]} ${styles[variant]} ${className}`}>
      {children}
    </button>
  );
}

export function Card({ children, className = "", hoverable = false, testid }) {
  return (
    <div data-testid={testid} className={`bg-white border border-slate-200 rounded-lg shadow-sm ${hoverable ? "hover:border-slate-300 hover:shadow-md transition-all" : ""} ${className}`}>
      {children}
    </div>
  );
}

/**
 * SectionCard - Card com header e body para paginas de Definicoes.
 * Uso: <SectionCard title="Titulo" hint="descricao opcional">...conteudo...</SectionCard>
 * Padrao visual das paginas de configuracao. Ver /app/design_guidelines.md.
 */
export function SectionCard({ title, hint, action, children, testid, className = "" }) {
  return (
    <section data-testid={testid} className={`bg-white border border-tint rounded-xl overflow-hidden ${className}`}>
      <header className="px-4 py-2.5 border-b border-tint bg-slate-50 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h4 className="text-navy font-semibold text-sm">{title}</h4>
          {hint && <p className="text-[11px] text-muted2 mt-0.5">{hint}</p>}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

/**
 * SettingsHero - Cartao hero no topo de paginas de Definicoes, com titulo,
 * descricao, badge de estado opcional e barra de accoes principais.
 */
export function SettingsHero({ icon, iconLabel = "", title, description, badge, actions, testid }) {
  return (
    <div data-testid={testid} className="bg-gradient-to-br from-blue/5 via-white to-slate-50 border border-blue/20 rounded-xl p-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            {icon ? icon : (iconLabel ? (
              <div className="w-7 h-7 rounded-lg bg-blue/10 text-blue flex items-center justify-center text-[11px] font-bold">{iconLabel}</div>
            ) : null)}
            <h3 className="text-navy font-bold text-base">{title}</h3>
          </div>
          {description && <p className="text-[12px] text-slate-700 leading-relaxed">{description}</p>}
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          {badge && badge}
          {actions && <div className="flex gap-1.5">{actions}</div>}
        </div>
      </div>
    </div>
  );
}

/**
 * StickyActionsBar - Rodape sticky com botoes de accao secundarios. Usado no
 * fim das paginas de Definicoes para acesso rapido enquanto se rola a pagina.
 */
export function StickyActionsBar({ children, hint, testid }) {
  return (
    <div data-testid={testid} className="sticky bottom-0 bg-white/95 backdrop-blur border-t border-tint py-3 flex flex-wrap items-center gap-2 -mx-2 px-2 z-10">
      {children}
      {hint && <span className="ml-auto text-[11px] text-muted2">{hint}</span>}
    </div>
  );
}

export function KpiCard({ icon: Icon, label, value, sub, accent = "navy", testid, onClick }) {
  const accentMap = {
    navy: "text-navy bg-slate-50",
    blue: "text-blue bg-blue-50",
    green: "text-emerald-700 bg-emerald-50",
    amber: "text-amber-700 bg-amber-50",
    red: "text-red-700 bg-red-50",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="text-left bg-white border border-slate-200 rounded-lg p-4 shadow-sm hover:border-slate-300 hover:shadow-md transition-all flex flex-col gap-1 min-h-[92px] focus:outline-none focus:ring-2 focus:ring-blue/30"
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</span>
        {Icon && <span className={`p-1.5 rounded-md ${accentMap[accent] || accentMap.navy}`}><Icon size={14} /></span>}
      </div>
      <div className="text-[22px] font-bold text-navy leading-none tracking-tight">{value}</div>
      {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
    </button>
  );
}

// Filtro em pills (ClickUp-style) - usado por cima de listas/tabelas
export function FilterPills({ options, value, onChange, testid }) {
  return (
    <div className="inline-flex gap-1 flex-wrap" data-testid={testid}>
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          data-testid={testid ? `${testid}-${opt.value}` : undefined}
          className={`text-[12px] px-2.5 py-1 rounded-full border transition-colors ${
            value === opt.value
              ? "bg-blue text-white border-blue shadow-sm"
              : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
          }`}
        >
          {opt.label}{typeof opt.count === "number" && <span className="ml-1 opacity-70">{opt.count}</span>}
        </button>
      ))}
    </div>
  );
}

// Icones por tipo de item de timeline (contas/contactos CRM)
export const TIPO_ICON = {
  "oportunidade": Target, "subscricao": Repeat, "ticket": Ticket, "fatura": FileText,
  "proposta": FileSignature, "contrato": ScrollText, "email": Mail,
  "atividade:chamada": Phone, "atividade:email": Mail, "atividade:reuniao": Calendar,
  "atividade:demo": Calendar, "atividade:formulario": FileText, "atividade:nota": StickyNote,
  "atividade:tarefa": StickyNote, "atividade:agendamento": Calendar, "atividade:chat": MessageSquare,
  "atividade:chat-ai": MessageSquare, "atividade:handoff": Users,
  "atividade:sms-inbound": Phone, "atividade:sms-outbound": Send, "atividade:sms": Phone,
  "utilizador": KeyRound,
};

// Chamadas sem sucesso ganham um icone grande e colorido para se destacarem na timeline
export const CALL_RESULT_META = {
  // resultados de entrada manual CRM
  nao_atendeu: { label: "Nao atendeu", icon: PhoneMissed, dot: "bg-red-600", ring: "ring-red-200", badge: "bg-red-100 text-red-700 border-red-300" },
  contactar_mais_tarde: { label: "Contactar mais tarde", icon: Clock, dot: "bg-amber-500", ring: "ring-amber-200", badge: "bg-amber-100 text-amber-700 border-amber-300" },
  pessoa_indisponivel: { label: "Pessoa responsavel indisponivel", icon: UserX, dot: "bg-orange-500", ring: "ring-orange-200", badge: "bg-orange-100 text-orange-700 border-orange-300" },
  // resultados do LetsCall One (disposition mapeado no webhook)
  atendeu: { label: "Atendida", icon: PhoneCall, dot: "bg-emerald-600", ring: "ring-emerald-200", badge: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  ocupado: { label: "Linha ocupada", icon: PhoneOff, dot: "bg-amber-600", ring: "ring-amber-200", badge: "bg-amber-100 text-amber-700 border-amber-300" },
  caixa_voz: { label: "Caixa de voz", icon: Voicemail, dot: "bg-slate-500", ring: "ring-slate-200", badge: "bg-slate-100 text-slate-700 border-slate-300" },
};

/**
 * TimelineMiniList - lista compacta de atividade recente (usada nos drawers
 * de conta/contacto). Destaca chamadas sem sucesso com icone grande vermelho/amarelo.
 */
export function TimelineMiniList({ items, testid = "timeline-mini" }) {
  if (!items || items.length === 0) {
    return <div className="text-[12px] text-slate-400 italic py-1" data-testid={`${testid}-empty`}>Sem atividade registada.</div>;
  }
  return (
    <div className="space-y-1.5" data-testid={testid}>
      {items.map((it, i) => {
        const Icon = TIPO_ICON[it.tipo] || StickyNote;
        const callMeta = CALL_RESULT_META[it.resultado];
        const isChamada = it.tipo === "atividade:chamada";
        const meta = it.meta || {};
        const durSec = meta.duration_seconds;
        const durStr = durSec != null ? (durSec >= 60 ? `${Math.floor(durSec / 60)}m${durSec % 60}s` : `${durSec}s`) : null;
        const extNum = meta.direction === "outbound" ? meta.callee_number : meta.caller_number;
        const DirecaoIcon = meta.direction === "outbound" ? PhoneOutgoing : (meta.direction === "inbound" ? PhoneIncoming : null);
        return (
          <div key={i} className="flex items-start gap-2.5 py-1.5 border-b border-slate-100 last:border-b-0" data-testid={`${testid}-item-${i}`}>
            {callMeta ? (
              <div className={`mt-0.5 w-7 h-7 rounded-full ${callMeta.dot} ring-2 ${callMeta.ring} flex items-center justify-center shrink-0`} title={callMeta.label}>
                <callMeta.icon size={14} className="text-white" />
              </div>
            ) : (
              <div className="mt-0.5 w-6 h-6 rounded-full bg-blue/10 flex items-center justify-center shrink-0">
                <Icon size={12} className="text-blue" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-[12px] font-semibold text-navy truncate">{it.titulo}</div>
              <div className="text-[10px] text-slate-500">{(it.tipo || "").replace(/[:_]/g, " ")}</div>
              {/* Detalhes especificos de chamadas LetsCall One */}
              {isChamada && (extNum || durStr || it.resultado) && (
                <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                  {callMeta && (
                    <span className={`inline-flex items-center text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${callMeta.badge}`}>{callMeta.label}</span>
                  )}
                  {DirecaoIcon && <DirecaoIcon size={10} className="text-slate-400" title={meta.direction === "outbound" ? "Efetuada" : "Recebida"} />}
                  {extNum && <span className="text-[10px] text-slate-500 font-mono">{extNum}</span>}
                  {durStr && <span className="text-[10px] text-slate-400 bg-slate-100 rounded px-1">{durStr}</span>}
                </div>
              )}
              {/* Fallback para resultados de chamadas manuais sem meta */}
              {callMeta && !isChamada && (
                <span className={`inline-flex items-center text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border mt-0.5 ${callMeta.badge}`}>{callMeta.label}</span>
              )}
              {it.detalhe && !isChamada && <div className="text-[11px] text-slate-600 truncate">{it.detalhe}</div>}
            </div>
            <div className="text-[10px] text-slate-400 shrink-0 whitespace-nowrap">{dt(it.em)}</div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * AccountActivityPreview - busca e mostra as ultimas atividades de uma conta.
 * Usado no AccountDrawer (painel lateral) para dar visibilidade sem abrir a ficha completa.
 */
export function AccountActivityPreview({ accountId, limit = 5 }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!accountId) return;
    let cancel = false;
    api.get(`/crm/accounts/${accountId}/timeline`).then((r) => {
      if (!cancel) setItems((r.data || []).slice(0, limit));
    }).catch(() => !cancel && setItems([]));
    return () => { cancel = true; };
  }, [accountId, limit]);
  if (items === null) return <div className="text-[12px] text-slate-400 py-1">A carregar...</div>;
  return <TimelineMiniList items={items} testid="drawer-account-activity" />;
}

/**
 * ContactActivityPreview - busca e mostra as ultimas atividades de um contacto.
 * Usado no ContactDrawer (painel lateral).
 */
export function ContactActivityPreview({ contactId, limit = 5 }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!contactId) return;
    let cancel = false;
    api.get(`/crm/contacts/${contactId}/timeline`).then((r) => {
      if (!cancel) setItems((r.data || []).slice(0, limit));
    }).catch(() => !cancel && setItems([]));
    return () => { cancel = true; };
  }, [contactId, limit]);
  if (items === null) return <div className="text-[12px] text-slate-400 py-1">A carregar...</div>;
  return <TimelineMiniList items={items} testid="drawer-contact-activity" />;
}
